/**
 * Sessieverlies-signaal voor de client.
 *
 * De app is local-first: een verlopen of ingetrokken sessie valt niet op totdat een
 * schrijfactie faalt (Marijn-incident, sept 2026: 10 minuten 401's op de achtergrond,
 * daarna een misleidende "groepssleutel niet gedeeld"-melding). Elke 401 van een eigen
 * API-route komt hier binnen; de SessionLostGuard bevestigt via /api/auth/session en
 * stuurt dan naar het inlogscherm. Vals alarm (één hikkende call) leidt dus nooit tot
 * een redirect.
 */

export const SESSION_LOST_EVENT = 'baya:session-lost';
/** Wordt pas gestuurd ná bevestiging; dialogen bewaren hierop hun concept. */
export const SESSION_LOST_CONFIRMED_EVENT = 'baya:session-lost-confirmed';

const DEBOUNCE_MS = 15_000;
let lastNotifiedAt = 0;

/** Routes waarvan een 401 géén sessieverlies betekent (login-flow, publieke API's). */
export function isSessionLostCandidate(url: string): boolean {
  let path = url;
  try {
    path = new URL(url, 'http://local').pathname;
  } catch {
    /* relatieve URL zonder parse */
  }
  if (!path.startsWith('/api/')) return false;
  if (path.startsWith('/api/auth/')) return false;
  if (path.startsWith('/api/public/') || path.startsWith('/api/website/')) return false;
  return true;
}

export function notifySessionLost(source: string, now: number = Date.now()): boolean {
  if (typeof window === 'undefined') return false;
  if (lastNotifiedAt > 0 && now - lastNotifiedAt < DEBOUNCE_MS) return false;
  lastNotifiedAt = now;
  window.dispatchEvent(new CustomEvent(SESSION_LOST_EVENT, { detail: { source } }));
  return true;
}

/** Alleen voor tests. */
export function _resetSessionLostDebounce(): void {
  lastNotifiedAt = 0;
}

export function onSessionLost(handler: (source: string) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<{ source?: string }>).detail?.source ?? 'unknown');
  window.addEventListener(SESSION_LOST_EVENT, listener);
  return () => window.removeEventListener(SESSION_LOST_EVENT, listener);
}

export function onSessionLostConfirmed(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(SESSION_LOST_CONFIRMED_EVENT, handler);
  return () => window.removeEventListener(SESSION_LOST_CONFIRMED_EVENT, handler);
}

/** Inlogscherm passend bij de huidige plek: mobiele shell heeft een eigen loginpagina. */
export function loginPathFor(pathname: string, search: string = ''): string {
  if (pathname === '/m' || pathname.startsWith('/m/')) return '/m/login?reason=expired';
  const params = new URLSearchParams({ reason: 'expired' });
  if (pathname && pathname !== '/') params.set('redirect', `${pathname}${search}`);
  return `/login?${params.toString()}`;
}

/** Op deze pagina's nooit redirecten (anders lus of onderbroken loginflow). */
export function isLoginLikePath(pathname: string): boolean {
  return (
    pathname === '/login' ||
    pathname.startsWith('/login/') ||
    pathname === '/m/login' ||
    pathname.startsWith('/m/login/') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/invite') ||
    pathname.startsWith('/g/')
  );
}
